import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '../api/client.js';
import type { AuditEntry } from '../api/types.js';
import { AUDIT_ACTION_LIST } from '../../shared/auditActions.js';
import {
  Button,
  Card,
  EmptyState,
  Field,
  PageHeader,
  Select,
  Spinner,
  TextInput,
} from '../components/ui.js';
import { formatDateTime, humanizeAction } from '../lib/format.js';

const PAGE_SIZE = 50;

export function AuditLogPage() {
  const [filters, setFilters] = useState({ action: '', from: '', to: '' });
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['audit-log', filters, offset],
    queryFn: () =>
      apiRequest<{ total: number; entries: AuditEntry[] }>('/api/admin/audit-log', {
        query: {
          limit: PAGE_SIZE,
          offset,
          ...(filters.action ? { action: filters.action } : {}),
          ...(filters.from ? { from: `${filters.from}T00:00:00Z` } : {}),
          ...(filters.to ? { to: `${filters.to}T23:59:59Z` } : {}),
        },
      }),
  });

  const total = data?.total ?? 0;

  return (
    <>
      <PageHeader
        title="Audit log"
        subtitle="Every recorded action, in order. Entries are append-only and cannot be edited or removed."
      />

      <Card className="mb-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <Field label="Action" htmlFor="auditAction">
            <Select
              id="auditAction"
              value={filters.action}
              onChange={(event) => {
                setOffset(0);
                setFilters({ ...filters, action: event.target.value });
              }}
            >
              <option value="">All actions</option>
              {AUDIT_ACTION_LIST.map((action) => (
                <option key={action} value={action}>
                  {humanizeAction(action)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="From" htmlFor="auditFrom">
            <TextInput
              id="auditFrom"
              type="date"
              value={filters.from}
              onChange={(event) => {
                setOffset(0);
                setFilters({ ...filters, from: event.target.value });
              }}
            />
          </Field>
          <Field label="To" htmlFor="auditTo">
            <TextInput
              id="auditTo"
              type="date"
              value={filters.to}
              onChange={(event) => {
                setOffset(0);
                setFilters({ ...filters, to: event.target.value });
              }}
            />
          </Field>
          <div className="flex items-end">
            <Button
              variant="ghost"
              onClick={() => {
                setFilters({ action: '', from: '', to: '' });
                setOffset(0);
              }}
            >
              Clear
            </Button>
          </div>
        </div>
      </Card>

      <Card bodyClassName="px-0 py-0 sm:px-0">
        {isLoading ? (
          <div className="p-4">
            <Spinner label="Loading audit log" />
          </div>
        ) : (data?.entries.length ?? 0) === 0 ? (
          <EmptyState title="No entries match" description="Adjust the filters above." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <caption className="sr-only">Audit log entries</caption>
              <thead className="bg-slate-50">
                <tr>
                  <th scope="col" className="table-header-cell">
                    When
                  </th>
                  <th scope="col" className="table-header-cell">
                    Action
                  </th>
                  <th scope="col" className="table-header-cell">
                    Actor
                  </th>
                  <th scope="col" className="table-header-cell">
                    Work order
                  </th>
                  <th scope="col" className="table-header-cell">
                    Detail
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data?.entries.map((entry) => (
                  <tr key={entry.id} className="align-top">
                    <td className="table-cell-base whitespace-nowrap">
                      {formatDateTime(entry.occurredAt)}
                    </td>
                    <td className="table-cell-base">{humanizeAction(entry.action)}</td>
                    <td className="table-cell-base">
                      {entry.actorName ?? entry.actorEmail ?? 'System'}
                      {entry.ipAddress && (
                        <span className="block text-xs text-slate-500">{entry.ipAddress}</span>
                      )}
                    </td>
                    <td className="table-cell-base">
                      {entry.workOrderId && entry.workOrderNumber ? (
                        <Link
                          to={`/work-orders/${entry.workOrderId}`}
                          className="font-mono text-xs text-brand-800 hover:underline"
                        >
                          {entry.workOrderNumber}
                        </Link>
                      ) : (
                        <span className="text-slate-500">{entry.entityType}</span>
                      )}
                    </td>
                    <td className="table-cell-base">
                      {entry.previousValue || entry.newValue || entry.metadata ? (
                        <>
                          <button
                            type="button"
                            className="text-xs text-brand-800 hover:underline"
                            onClick={() =>
                              setExpanded((previous) => (previous === entry.id ? null : entry.id))
                            }
                          >
                            {expanded === entry.id ? 'Hide' : 'Show'} recorded values
                          </button>
                          {expanded === entry.id && (
                            <pre className="mt-1 max-w-xl overflow-x-auto rounded bg-slate-900 p-2 text-xs text-slate-100">
                              {JSON.stringify(
                                {
                                  previous: entry.previousValue,
                                  next: entry.newValue,
                                  metadata: entry.metadata,
                                },
                                null,
                                2,
                              )}
                            </pre>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-slate-500">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="mt-3 flex items-center justify-between">
        <p className="text-sm text-slate-600">
          {total === 0
            ? 'No entries'
            : `Showing ${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} of ${total}`}
        </p>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      </div>
    </>
  );
}
